import React, {useCallback, useEffect} from "react";
import {useAppDispatch, useAppSelector} from "../../redux/store";
import {
    changeCitySFAC,
    changeDateSFAC,
    changeMarketSFAC, changeOrderingPriceSFAC,
    changeOrderingUnicSFAC,
    fetchSkuFoodTC
} from "../../redux/skuFoodReducer";
import {selectSkuFood} from "../../common/selectors";
import {SkuFoodTable} from "./skuFoodTable";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/ru_RU";
import dateIcon from "../../assets/datePickerIcon.svg";
import closeIcon from "../../assets/close.svg";
import {Checkbox, DatePicker, DatePickerProps, Radio, RadioChangeEvent} from "antd";
import {RangePickerProps} from "antd/es/date-picker";
import {citiesSF, marketsSF} from "../../common/optionsForSkuFood";
import {CheckboxValueType} from "antd/es/checkbox/Group";
import {CollapseSettingComp} from "../../components/collapseSettingComp";
import styles from './skuFood.module.sass'
import {Spinner} from "../../common/spin";

// Компонент страницы SKU Food, отображает фильтры, таблицу и логику загрузки данных
export const SkuFood = () => {
    // Деструктурируем RangePicker из DatePicker
    const {RangePicker} = DatePicker;

    // Получаем dispatch для работы с redux
    const dispatch = useAppDispatch();
    // Получаем необходимые данные из redux-стора
    const {data, loading, offset, error, date, cities, store_name, orderingUnic, orderingPrice} = useAppSelector(selectSkuFood);

    // Формируем строку городов для запроса
    const citiesReq = cities.length ? cities.join(',') : ''
    // Формируем строку маркетов для запроса
    const marketsReq = store_name.length ? store_name.join(',') : ''

    // Массив сортировок для запроса
    const ordering: string[] = []
    if (orderingUnic) ordering.push(orderingUnic)
    if (orderingPrice) ordering.push(orderingPrice)

    // Загрузка данных при изменении даты или offset
    useEffect(() => {
        dispatch(fetchSkuFoodTC({
            offset: 0,
            start_date_from: date[0],
            end_date_to: date[1],
            city: citiesReq,
            store_name: marketsReq,
            ordering: ordering.join(','),
            address: ''
        })); // Сбрасываем offset при изменении даты
    }, [dispatch, date, cities, store_name, orderingUnic, orderingPrice]);

    // Подгрузка данных при прокрутке
    const loadMore = useCallback(() => {
        if (loading !== "pending") {
            dispatch(fetchSkuFoodTC({
                offset,
                start_date_from: date[0],
                end_date_to: date[1],
                city: citiesReq,
                store_name: marketsReq,
                ordering: ordering.join(','),
                address: ''
            }));
        }
    }, [dispatch, offset, loading, date, cities, store_name, orderingUnic, orderingPrice]);

    // Кастомное форматирование даты для RangePicker
    const customFormat = (value: any) => {
        return value.format("YYYY-MM-DD ");
    };

    // Обработчик изменения диапазона дат
    const onChange = (
        value: DatePickerProps['value'] | RangePickerProps['value'],
        dateString: [string, string] | string,
    ) => {
        if (Array.isArray(dateString)) {
            const startOfWeek = dayjs(dateString[0]).startOf('week').add(0, 'day');
            const endOfWeek = dayjs(dateString[1]).endOf('week').add(0, 'day');

            const start = startOfWeek.format('YYYY-MM-DD');
            const end = endOfWeek.format('YYYY-MM-DD');

            dispatch(changeDateSFAC([start, end])); // Изменяем дату и сбрасываем данные
        }
    };

    // Обработчик изменения выбранных маркетов
    const onChangeCheckMarket = (checkedValues: CheckboxValueType[]) => {
        dispatch(changeMarketSFAC(checkedValues))
    }

    // Обработчик изменения выбранных городов
    const onChangeCheckCities = (checkedValues: CheckboxValueType[]) => {
        dispatch(changeCitySFAC(checkedValues))
    }

    // Обработчик сортировки по уникальности
    const onUnicChange = ({ target: { value } }: RadioChangeEvent) => {
        dispatch(changeOrderingUnicSFAC(value))
    }

    // Обработчик сортировки по цене
    const onPriceChange = ({ target: { value } }: RadioChangeEvent) => {
        dispatch(changeOrderingPriceSFAC(value))
    }

    // Рендер страницы: фильтры, таблица, спиннер
    return (
        <>
            {/* Визуальный разделитель */}
            <div className={styles.divider}></div>
            <div style={{position: 'relative', marginBottom: 10}}>
                {/* Фильтр по дате */}
                <RangePicker
                    defaultValue={[dayjs(date[0]), dayjs(date[1])]}
                    style={{width: '20%', marginRight: 20}}
                    picker={'week'}
                    //value={[dayjs(date[0]), dayjs(date[1])]}
                    locale={locale}
                    suffixIcon={<img src={dateIcon} alt={'select'}/>}
                    allowClear={{
                        clearIcon: <img className={styles.closeIcon} src={closeIcon} alt={'clear'}/>,
                    }}
                    format={customFormat}
                    onChange={onChange}
                />
                {/* Фильтры и сортировки */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    gap: 20,
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    zIndex: 10
                }}>
                    {/* Сортировка по цене */}
                    <CollapseSettingComp name={'Цена'} type={'Сорт.'}>
                        <Radio.Group onChange={onPriceChange} style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            flexDirection: 'column',
                            gap: '10px',
                            color: "white"
                        }} options={[
                            {label: 'Все', value: ''},
                            {label: '↑ Цена витринная', value: '-price_vit'},
                            {label: '↓ Цена витринная', value: 'price_vit'},
                            {label: '↑ Скидка', value: '-price_sale'},
                            {label: '↓ Скидка', value: 'price_sale'},
                        ]} value={orderingPrice}/>
                    </CollapseSettingComp>
                    {/* Сортировка по уникальности */}
                    <CollapseSettingComp name={'Уникальность'} type={'Сорт.'}>
                        <Radio.Group onChange={onUnicChange} style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            flexDirection: 'column',
                            gap: '10px',
                            color: "white"
                        }} options={[{label: 'Все', value: ''}, {
                            label: 'Повтор',
                            value: 'unic'
                        }, {label: 'Уникальный', value: '-unic'},]} value={orderingUnic}/>
                    </CollapseSettingComp>
                    {/* Фильтр по маркету */}
                    <CollapseSettingComp name={'Маркет'} type={'Фильтр'}>
                        <Checkbox.Group onChange={onChangeCheckMarket} defaultValue={store_name} style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            flexDirection: 'column',
                            gap: '10px'
                        }}>
                            {marketsSF.map((el) => <div key={el}><Checkbox name={el} value={el}/><span
                                style={{marginLeft: 5}}>{el}</span></div>)}
                        </Checkbox.Group>
                    </CollapseSettingComp>
                    {/* Фильтр по городу */}
                    <CollapseSettingComp name={'Город'} type={'Фильтр'}>
                        <Checkbox.Group onChange={onChangeCheckCities} defaultValue={cities} style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            flexDirection: 'column',
                            gap: '10px'
                        }}>
                            {citiesSF.map((el) => <div key={el}><Checkbox name={el} value={el}/><span
                                style={{marginLeft: 5}}>{el}</span></div>)}
                        </Checkbox.Group>
                    </CollapseSettingComp>
                </div>
            </div>
            {/* Таблица или спиннер при загрузке */}
            {data.results.length ? <SkuFoodTable data={data.results} callBack={loadMore}/> : <div style={{height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center'}}><Spinner/></div>}
        </>
    );
};
